import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectAccess, selectRole } from "../store/authSlice";

export const ROLES = {
  OWNER: "Owner",
  ADMIN: "Admin",
  DEVELOPER: "Developer",
  CLIENT: "Client",
};

export const MODULES = {
  DASHBOARD: "Dashboard",
  CLIENTS: "Clients",
  PACKAGES: "Packages",
  SUBSCRIPTIONS: "Subscriptions",
  PAYMENTS: "Payments",
  INVOICES: "Invoices",
  AGENTS: "Agents",
  LLM_MODELS: "LlmModels",
  LLM_KEYS: "LlmKeys",
  API_KEYS: "ApiKeys",
  FEEDBACKS: "Feedbacks",
  TOKEN_VALUES: "TokenValues",
  REPORTS: "Reports",
  LOGS: "Logs",
  WEBHOOKS: "Webhooks",
  USERS: "Users",
};

const ACTION_FLAG = {
  view: "canView",
  create: "canCreate",
  update: "canUpdate",
  delete: "canDelete",
};

/**
 * Mirrors the server-side rule in UserModulePermission.Allows: a write grant implies
 * read, so a developer given only "create" still sees the screen.
 */
const allows = (permission, action) => {
  if (!permission) return false;
  if (action === "view") {
    return Boolean(
      permission.canView ||
        permission.canCreate ||
        permission.canUpdate ||
        permission.canDelete
    );
  }
  return Boolean(permission[ACTION_FLAG[action]]);
};

/**
 * Access helpers driven by GET /auth/me. This only hides UI — every endpoint
 * re-checks the same rules server-side, so a stale cache cannot grant anything.
 */
export function useAccess() {
  const access = useSelector(selectAccess);
  const role = useSelector(selectRole);

  const permissionsByModule = useMemo(() => {
    const map = {};
    (access?.permissions || []).forEach((p) => {
      map[p.module] = p;
    });
    return map;
  }, [access]);

  const isUnrestricted = Boolean(access?.isUnrestricted) ||
    role === ROLES.OWNER ||
    role === ROLES.ADMIN;

  const can = useCallback(
    (module, action = "view") => {
      if (isUnrestricted) return true;
      if (role !== ROLES.DEVELOPER) return false;
      return allows(permissionsByModule[module], action);
    },
    [isUnrestricted, role, permissionsByModule]
  );

  return {
    access,
    role,
    can,
    isUnrestricted,
    isClient: role === ROLES.CLIENT,
    isDeveloper: role === ROLES.DEVELOPER,
    companyId: access?.companyId ?? null,
    companyName: access?.companyName ?? null,
    allowedModules: access?.allowedModules || [],
  };
}

export default useAccess;
